import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Layout from '../components/layout/Layout';
import { useAtm } from '../context/AtmContext';
import { useAuth } from '../hooks/useAuth';
import { Atm, AtmOperationType, AtmOption, AtmTheme } from '../types/atm';

/**
 * Page for editing an existing ATM
 */
const EditAtm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { fetchAtm, updateExistingAtm, error, clearError } = useAtm();
  const { currentUser } = useAuth();

  // State for ATM data
  const [atm, setAtm] = useState<Atm | null>(null);
  const [atmName, setAtmName] = useState<string>('');
  const [atmDescription, setAtmDescription] = useState<string>('');
  const [atmTheme, setAtmTheme] = useState<AtmTheme>(AtmTheme.DEFAULT);
  const [isPublic, setIsPublic] = useState<boolean>(false);
  const [requirePin, setRequirePin] = useState<boolean>(true);
  const [defaultPin, setDefaultPin] = useState<string>('1234');
  const [options, setOptions] = useState<AtmOption[]>([]);

  // UI state
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Load ATM data
  useEffect(() => {
    const loadAtm = async () => {
      if (!id) {
        navigate('/my-atms');
        return;
      }

      try {
        setLoading(true);
        const atmData = await fetchAtm(id, true);

        if (!atmData) {
          navigate('/my-atms');
          return;
        }

        // Check if user owns this ATM
        if (
          currentUser &&
          atmData.createdBy !== currentUser.uid &&
          atmData.createdBy !== 'anonymous'
        ) {
          navigate('/my-atms');
          return;
        }

        setAtm(atmData);
        setAtmName(atmData.name);
        setAtmDescription(atmData.description || '');
        setAtmTheme(atmData.theme);
        setIsPublic(atmData.isPublic);
        setRequirePin(atmData.atmSettings.requirePin);
        setDefaultPin(atmData.atmSettings.defaultPin || '1234');
        setOptions([...atmData.options]);
      } catch (err) {
        console.error('Error loading ATM:', err);
        navigate('/my-atms');
      } finally {
        setLoading(false);
      }
    };

    loadAtm();
  }, [id, navigate, currentUser]); // Remove fetchAtm dependency to prevent infinite loop

  /**
   * Validate ATM data
   */
  const validateAtm = (): boolean => {
    if (!atmName.trim()) {
      setValidationError('Please enter a name for your ATM');
      return false;
    }

    if (options.filter(opt => opt.enabled).length < 2) {
      setValidationError('Please enable at least 2 ATM options');
      return false;
    }

    if (requirePin && (!defaultPin || defaultPin.length < 4)) {
      setValidationError('PIN must be at least 4 characters');
      return false;
    }

    setValidationError(null);
    return true;
  };

  /**
   * Handle saving the ATM
   */
  const handleSaveAtm = async () => {
    if (!atm || !validateAtm()) return;

    try {
      setSaving(true);
      clearError();

      const updatedAtmData: Partial<Atm> = {
        name: atmName.trim(),
        description: atmDescription.trim(),
        theme: atmTheme,
        isPublic,
        options: options.filter(opt => opt.enabled),
        atmSettings: {
          requirePin,
          defaultPin: requirePin ? defaultPin : undefined,
          animationSpeed: atm.atmSettings.animationSpeed || 1,
          showInExplore: isPublic,
          sounds: atm.atmSettings.sounds || { enabled: true },
        },
      };

      await updateExistingAtm(atm.id, updatedAtmData);

      // Navigate back to ATM detail
      navigate(`/atm/${atm.id}`);
    } catch (err) {
      console.error('Error updating ATM:', err);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Add a new ATM option
   */
  const addOption = () => {
    const newOption: AtmOption = {
      text: 'New Option',
      type: AtmOperationType.WITHDRAWAL,
      amount: 10,
      enabled: true,
      position: options.length,
    };
    setOptions([...options, newOption]);
  };

  /**
   * Update an ATM option
   */
  const updateOption = (index: number, updatedOption: Partial<AtmOption>) => {
    const newOptions = [...options];
    newOptions[index] = { ...newOptions[index], ...updatedOption };
    setOptions(newOptions);
  };

  /**
   * Remove an ATM option
   */
  const removeOption = (index: number) => {
    const newOptions = options.filter((_, i) => i !== index);
    // Update positions
    newOptions.forEach((option, i) => {
      option.position = i;
    });
    setOptions(newOptions);
  };

  /**
   * Move option up or down
   */
  const moveOption = (index: number, direction: 'up' | 'down') => {
    const newOptions = [...options];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex >= 0 && targetIndex < newOptions.length) {
      // Swap the options
      [newOptions[index], newOptions[targetIndex]] = [newOptions[targetIndex], newOptions[index]];
      // Update positions
      newOptions[index].position = index;
      newOptions[targetIndex].position = targetIndex;
      setOptions(newOptions);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <p className="mt-4 text-gray-600">Loading ATM...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Edit ATM</h1>
          <p className="text-gray-600">Update your ATM settings and options.</p>
        </div>

        {/* Error Display */}
        {(error || validationError) && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-700">{error || validationError}</p>
          </div>
        )}

        <div className="space-y-8">
          {/* Basic Information */}
          <div className="bg-white shadow-sm rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="atmName">
                  ATM Name *
                </label>
                <input
                  id="atmName"
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter ATM name"
                  value={atmName}
                  onChange={e => setAtmName(e.target.value)}
                  maxLength={50}
                />
                <p className="text-xs text-gray-500 mt-1">{atmName.length}/50 characters</p>
              </div>

              <div>
                <label
                  className="block text-sm font-medium text-gray-700 mb-2"
                  htmlFor="atmDescription"
                >
                  Description (Optional)
                </label>
                <textarea
                  id="atmDescription"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Describe your ATM"
                  value={atmDescription}
                  onChange={e => setAtmDescription(e.target.value)}
                  rows={3}
                  maxLength={200}
                />
                <p className="text-xs text-gray-500 mt-1">{atmDescription.length}/200 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Theme</label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {Object.values(AtmTheme).map(theme => (
                    <button
                      key={theme}
                      type="button"
                      className={`p-3 border-2 rounded-lg text-sm font-medium transition-colors ${
                        atmTheme === theme
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                      }`}
                      onClick={() => setAtmTheme(theme)}
                    >
                      {theme.charAt(0).toUpperCase() + theme.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center">
                <input
                  id="isPublic"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  checked={isPublic}
                  onChange={e => setIsPublic(e.target.checked)}
                />
                <label htmlFor="isPublic" className="ml-2 block text-sm text-gray-700">
                  Make this ATM public (visible to others)
                </label>
              </div>
            </div>
          </div>

          {/* Options Configuration */}
          <div className="bg-white shadow-sm rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-900">ATM Options</h2>
              <button
                type="button"
                onClick={addOption}
                className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Add Option
              </button>
            </div>

            <div className="space-y-4">
              {options.map((option, index) => (
                <div
                  key={index}
                  className={`p-4 border rounded-lg ${
                    option.enabled ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50'
                  }`}
                >
                  <div className="flex items-start space-x-4">
                    <div className="flex-1 space-y-4">
                      <div className="flex items-center space-x-4">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          checked={option.enabled}
                          onChange={e => updateOption(index, { enabled: e.target.checked })}
                        />
                        <input
                          type="text"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Option text"
                          value={option.text}
                          onChange={e => updateOption(index, { text: e.target.value })}
                          disabled={!option.enabled}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Operation Type
                          </label>
                          <select
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={option.type}
                            onChange={e =>
                              updateOption(index, { type: e.target.value as AtmOperationType })
                            }
                            disabled={!option.enabled}
                          >
                            <option value={AtmOperationType.WITHDRAWAL}>Withdrawal</option>
                            <option value={AtmOperationType.DEPOSIT}>Deposit</option>
                            <option value={AtmOperationType.BALANCE}>Check Balance</option>
                            <option value={AtmOperationType.TRANSFER}>Transfer</option>
                            <option value={AtmOperationType.CANCEL}>Cancel</option>
                            <option value={AtmOperationType.CUSTOM}>Custom Amount</option>
                          </select>
                        </div>

                        {option.type !== AtmOperationType.BALANCE &&
                          option.type !== AtmOperationType.CANCEL && (
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Amount ($)
                              </label>
                              <input
                                type="number"
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="0"
                                value={option.amount || ''}
                                onChange={e =>
                                  updateOption(index, {
                                    amount: e.target.value ? parseFloat(e.target.value) : undefined,
                                  })
                                }
                                disabled={!option.enabled}
                                min="0"
                                step="0.01"
                              />
                            </div>
                          )}
                      </div>
                    </div>

                    <div className="flex flex-col space-y-2">
                      <button
                        type="button"
                        onClick={() => moveOption(index, 'up')}
                        disabled={index === 0}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => moveOption(index, 'down')}
                        disabled={index === options.length - 1}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        onClick={() => removeOption(index)}
                        className="p-1 text-red-400 hover:text-red-600"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {options.filter(opt => opt.enabled).length < 2 && (
              <p className="text-sm text-amber-600 mt-4">
                ⚠️ Enable at least 2 options for your ATM to function properly.
              </p>
            )}
          </div>

          {/* ATM Settings */}
          <div className="bg-white shadow-sm rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">ATM Settings</h2>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <input
                  id="requirePin"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  checked={requirePin}
                  onChange={e => setRequirePin(e.target.checked)}
                />
                <label htmlFor="requirePin" className="text-sm font-medium text-gray-700">
                  Require PIN for access
                </label>
              </div>

              {requirePin && (
                <div>
                  <label
                    className="block text-sm font-medium text-gray-700 mb-2"
                    htmlFor="defaultPin"
                  >
                    Default PIN
                  </label>
                  <input
                    id="defaultPin"
                    type="password"
                    className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter 4-digit PIN"
                    value={defaultPin}
                    onChange={e => setDefaultPin(e.target.value)}
                    maxLength={10}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This PIN will be required to access the ATM.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-between">
            <button
              type="button"
              onClick={() => navigate(`/atm/${id}`)}
              className="px-6 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleSaveAtm}
              disabled={saving}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default EditAtm;
